/**
 * The line, as a network.
 *
 * V2 held the geography as two module-level arrays (STATIONS, CUM_MIN) and a
 * pair of hard-coded station-index lists. Everything downstream — capacity,
 * crossing checks, stock balance, diagrams — read those directly, which is why
 * a second modelling mode could not be added without forking the engine.
 *
 * Here the geography is data. Two templates are shipped:
 *
 *   through  — Pickering->Whitby as one service group, no transfers. The V2
 *              geometry and timings unchanged.
 *   grosmont — Pickering<->Grosmont on our metals + Grosmont<->Whitby over
 *              Network Rail, with a transfer node at Grosmont.
 *
 * Journey times are the V2 figures (0/25/55/75/105 minutes from Pickering).
 *
 * Geography reconciled against thebigplan (the movement-planning source of
 * truth, `db/008_seed.sql` + `src/pages/ttbuilder/ttDerive.ts`):
 *   - The PASSING LOOPS are Levisham and Goathland only. Pickering, Grosmont
 *     and Whitby each have two roads, so they can hold a crossing move too;
 *     Newtondale is a request halt and is NOT a crossing point.
 *   - Grosmont is the MPD (sheds, coal, water) and has two roads — which is why
 *     both platforms can be occupied for a transfer at once.
 *   - Timing points are PIC / LEV / NEW / GOA / GRO / WHB, matching the 9-slot
 *     working-timetable grid. Glaisdale appears in thebigplan's station list
 *     flagged `esk: true` for Northern connections and is not on our path.
 *   - Mileages are the real railway ones: Pickering 0, Levisham 5.5,
 *     Goathland 12.5, Grosmont 18, Whitby 24.
 */
import type {
  Network, Segment, ServiceGroup, Station, StudioMode, Traction, TransferNode,
} from './types'

export const STATIONS_DEF: Station[] = [
  { code: 'PIC', name: 'Pickering', tt: 'PIC' },
  { code: 'LEV', name: 'Levisham',  tt: 'LEV' },
  { code: 'GOA', name: 'Goathland', tt: 'GOA' },
  { code: 'GRO', name: 'Grosmont',  tt: 'GRO' },
  { code: 'WHB', name: 'Whitby',    tt: 'WHB' },
]

/** Station index of the Network Rail boundary. */
export const GROSMONT = 3
export const WHITBY = 4
export const PICKERING = 0

/**
 * Running minutes (the V2 figures) and real railway mileages per segment.
 * Cumulative: 0 / 25 / 55 / 75 / 105 minutes and 0 / 5.5 / 12.5 / 18 / 24 miles.
 */
const BASE_SEGMENTS: { minutes: number; miles: number }[] = [
  { minutes: 25, miles: 5.5 }, // Pickering  - Levisham   (loop)
  { minutes: 30, miles: 7.0 }, // Levisham   - Goathland  (loop, via Newtondale halt)
  { minutes: 20, miles: 5.5 }, // Goathland  - Grosmont   (MPD, 2 roads)
  { minutes: 30, miles: 6.0 }, // Grosmont   - Whitby     (NETWORK RAIL)
]

// ---------------------------------------------------------------------------
//  Mode B defaults
// ---------------------------------------------------------------------------

/**
 * Paths/day over Grosmont-Whitby. Chris, 2026-07-30: "you have what we do now —
 * we don't want to be running any more than we need to." So the default is
 * DERIVED from the observed timetable (see observedNrTraversals) rather than
 * invented, and Mode B is held to the paths we already use. 0 here means
 * "derive it"; a positive number overrides.
 */
export const DEFAULT_NR_PATHS_PER_DAY = 0
/** Network Rail counts each traversal as a path. */
export const DEFAULT_NR_PATHS_PER_TRAVERSAL = 1
/**
 * Minimum connection time at Grosmont — MEASURED OPERATIONAL FIGURE, not an
 * assumption. Chris, 2026-07-30: 30 minutes to get everyone off the Grosmont
 * train and onto the Whitby train, EACH WAY. This is the single biggest cost of
 * Mode B and the number the whole idea lives or dies on.
 */
export const DEFAULT_MCT_MIN = 30
/** Both Grosmont platforms can be occupied at once (2 roads; Chris confirmed). */
export const DEFAULT_TRANSFER_PLATFORMS = 2
/** Generalised-time penalty of the change itself, equivalent minutes. */
export const DEFAULT_INTERCHANGE_PENALTY_MIN = 10
/** Waiting time weighted against in-vehicle time (rail norm 1.5-2.5). */
export const DEFAULT_WAIT_WEIGHT = 2
/** Share of through demand that refuses any change outright. ASSUMPTION. */
export const DEFAULT_AVERSION_SHARE = 0.12
/** The shuttle: diesel by default, coach count variable (Chris, 2026-07-30). */
export const DEFAULT_SHUTTLE_COACHES = 4

export const DEFAULT_TURNAROUND_MIN = 30
/** A short unit at a terminus turns far quicker than a 7-coach rake. */
export const DEFAULT_SHUTTLE_TURNAROUND_MIN = 12

/**
 * How quickly a locomotive (and the set turning with it) is demonstrably back
 * out after arrival — run round, brake test, away. MEASURED from the ss26
 * working timetable: G1 turns Whitby 09:45→10:00 (1T10→1T11) and the W2 engine
 * 16:55→17:10 (1T18→1T19), both 15 minutes; Pickering turns observed 20–23 min.
 * Distinct from DEFAULT_TURNAROUND_MIN, which is the timetable-DESIGN spacing:
 * chaining feasibility must use what the railway actually does, otherwise every
 * real 15-minute turn spawns a phantom engine — the D1 defect that scored the
 * August timetable at 5 steam diagrams against a fleet of 3.
 */
export const LOCO_TURN_MIN = 15

/**
 * The longest day one engine diagram may work, first departure to last arrival.
 * MEASURED: the longest ss26 diagram is 9 h 27 (W2's engine, 09:20→18:47), and
 * the evening Pullman gets a fresh engine even though G1 was physically at
 * Grosmont in time — footplate hours and prep/disposal bound a diagram. Without
 * this cap the chainer would merge the dining turn onto a day engine and claim
 * a fleet the railway would never roster.
 */
export const MAX_DIAGRAM_SPAN_MIN = 600

export interface NetworkOptions {
  nrPathsPerDay?: number
  nrPathsPerTraversal?: number
  mctMin?: number
  transferPlatforms?: number
  interchangePenaltyMin?: number
  waitWeight?: number
  aversionShare?: number
  /** Allow steam over Network Rail. Off by default — diesel is simpler to regulate. */
  steamOverNR?: boolean
  shuttleTurnaroundMin?: number
  whitbyTurnaroundMin?: number
}

function cumulative(segments: { minutes: number; miles: number }[]): { cumMin: number[]; cumMiles: number[] } {
  const cumMin = [0]
  const cumMiles = [0]
  for (const s of segments) {
    cumMin.push(cumMin[cumMin.length - 1] + s.minutes)
    cumMiles.push(cumMiles[cumMiles.length - 1] + s.miles)
  }
  return { cumMin, cumMiles }
}

/** Mode A — one through service group, no transfers. The V2 geometry exactly. */
export function throughNetwork(opts: NetworkOptions = {}): Network {
  const segments: Segment[] = BASE_SEGMENTS.map((s, i) => ({
    from: i,
    to: i + 1,
    minutes: s.minutes,
    miles: s.miles,
    owner: i === 3 ? 'networkrail' : 'nymr',
    // Through running consumes Network Rail paths too, so the cap applies in
    // BOTH modes — which is exactly what makes the A/B comparison fair: Mode B
    // has to live inside the same path budget we use today.
    pathsPerDay: i === 3 && (opts.nrPathsPerDay ?? 0) > 0 ? opts.nrPathsPerDay : undefined,
    pathsPerTraversal: i === 3 ? (opts.nrPathsPerTraversal ?? DEFAULT_NR_PATHS_PER_TRAVERSAL) : undefined,
  }))
  const { cumMin, cumMiles } = cumulative(BASE_SEGMENTS)
  const groups: ServiceGroup[] = [{
    key: 'main',
    label: 'Pickering – Whitby',
    from: 0,
    to: 4,
    allowedTraction: ['steam', 'diesel', 'railcar'],
    defaultCoaches: 7,
  }]
  return {
    mode: 'through',
    label: 'Through Running',
    stations: STATIONS_DEF,
    segments,
    cumMin,
    cumMiles,
    // Crossing points, per thebigplan's topology: the passing loops at Levisham
    // and Goathland, plus Pickering and Grosmont (two roads each). Whitby is
    // excluded in Mode A — Chris's explicit call, 2026-07-22 — because the
    // platform there is shared with Esk Valley services. Never mid-section.
    crossingPoints: [0, 1, 2, 3],
    stables: [0, 3],
    carriageStables: [0, 3],
    turnaround: opts.whitbyTurnaroundMin != null ? { 4: opts.whitbyTurnaroundMin } : {},
    defaultTurnaroundMin: DEFAULT_TURNAROUND_MIN,
    transfers: [],
    groups,
  }
}

/**
 * Mode B — main line on our metals + a Network Rail shuttle, changing at
 * Grosmont. A through working is still expressible (a service whose span
 * crosses the boundary belongs to the 'main' group and simply uses an NR path),
 * so a hybrid timetable — shuttles plus one flagship steam through-working — is
 * a first-class answer rather than a special case.
 */
export function grosmontNetwork(opts: NetworkOptions = {}): Network {
  const base = throughNetwork(opts)
  const shuttleTraction: Traction[] = opts.steamOverNR
    ? ['steam', 'diesel', 'railcar']
    : ['diesel', 'railcar']
  const groups: ServiceGroup[] = [
    {
      key: 'main',
      label: 'Pickering – Grosmont (NYMR)',
      from: 0,
      to: 3,
      allowedTraction: ['steam', 'diesel', 'railcar'],
      defaultCoaches: 7,
    },
    {
      key: 'shuttle',
      label: 'Grosmont – Whitby (Network Rail)',
      from: 3,
      to: 4,
      allowedTraction: shuttleTraction,
      defaultCoaches: DEFAULT_SHUTTLE_COACHES,
    },
  ]
  const transfers: TransferNode[] = [{
    station: GROSMONT,
    mctMin: opts.mctMin ?? DEFAULT_MCT_MIN,
    platforms: opts.transferPlatforms ?? DEFAULT_TRANSFER_PLATFORMS,
    penaltyMin: opts.interchangePenaltyMin ?? DEFAULT_INTERCHANGE_PENALTY_MIN,
    waitWeight: opts.waitWeight ?? DEFAULT_WAIT_WEIGHT,
    aversionShare: opts.aversionShare ?? DEFAULT_AVERSION_SHARE,
  }]
  return {
    ...base,
    mode: 'grosmont',
    label: 'Connect at Grosmont',
    // A shuttle service turns at Whitby many times a day, so the second road
    // there has to be usable. thebigplan's topology gives Whitby two roads.
    crossingPoints: [0, 1, 2, 3, 4],
    turnaround: {
      // The shuttle's turnaround at Whitby is what makes Mode B work at all: a
      // short unit is back out long before a 7-coach rake could be.
      [WHITBY]: opts.whitbyTurnaroundMin ?? opts.shuttleTurnaroundMin ?? DEFAULT_SHUTTLE_TURNAROUND_MIN,
    },
    transfers,
    groups,
  }
}

export function buildNetwork(mode: StudioMode, opts: NetworkOptions = {}): Network {
  return mode === 'grosmont' ? grosmontNetwork(opts) : throughNetwork(opts)
}

// ---------------------------------------------------------------------------
//  Derived helpers
// ---------------------------------------------------------------------------

export const stationNames = (n: Network): string[] => n.stations.map((s) => s.name)

export const journeyMin = (n: Network, o: number, d: number): number =>
  Math.abs(n.cumMin[d] - n.cumMin[o])

export const journeyMiles = (n: Network, o: number, d: number): number =>
  Math.abs(n.cumMiles[d] - n.cumMiles[o])

export const routeLabel = (n: Network, o: number, d: number): string =>
  `${n.stations[o].name} → ${n.stations[d].name}`

export const turnaroundAt = (n: Network, station: number): number =>
  n.turnaround[station] ?? n.defaultTurnaroundMin

/**
 * Turnaround for CHAINING (engine diagrams and set chains): the measured loco
 * turn, unless the network declares an even quicker station-specific turn
 * (the Mode B shuttle's 12 minutes at Whitby).
 */
export const locoTurnAt = (n: Network, station: number): number =>
  Math.min(LOCO_TURN_MIN, turnaroundAt(n, station))

/** Stations with no stabling — a rake must arrive before one can depart. */
export const nonStableStations = (n: Network): number[] =>
  n.stations.map((_, i) => i).filter((i) => !n.stables.includes(i))

/** Nearest station that CAN stable stock, for positioning moves. */
export function nearestStable(n: Network, station: number): number {
  let best = n.stables[0] ?? 0
  let bestDist = Infinity
  for (const s of n.stables) {
    const d = Math.abs(n.cumMin[s] - n.cumMin[station])
    if (d < bestDist) { bestDist = d; best = s }
  }
  return best
}

/** The service group a span belongs to — the narrowest group that contains it. */
export function groupFor(n: Network, o: number, d: number): ServiceGroup {
  const lo = Math.min(o, d)
  const hi = Math.max(o, d)
  const fits = n.groups
    .filter((g) => g.from <= lo && g.to >= hi)
    .sort((a, b) => (a.to - a.from) - (b.to - b.from))
  // A span crossing every group boundary (a through working in Mode B) has no
  // containing group; it belongs to the widest one and pays its constraints.
  return fits[0] ?? n.groups.reduce((a, b) => ((b.to - b.from) > (a.to - a.from) ? b : a))
}

export const groupKeyFor = (n: Network, o: number, d: number): string => groupFor(n, o, d).key

/** True when this span crosses a transfer node (so it needs a connection). */
export function crossesTransfer(n: Network, o: number, d: number): TransferNode | null {
  if (n.transfers.length === 0) return null
  const lo = Math.min(o, d)
  const hi = Math.max(o, d)
  for (const t of n.transfers) {
    if (t.station > lo && t.station < hi) {
      // Only a real boundary if no single group spans the whole journey.
      const spanned = n.groups.some((g) => g.from <= lo && g.to >= hi)
      if (!spanned) return t
    }
  }
  return null
}

/** Segment indices a span traverses. */
export function segmentsFor(n: Network, o: number, d: number): number[] {
  const lo = Math.min(o, d)
  const hi = Math.max(o, d)
  const out: number[] = []
  n.segments.forEach((s, i) => { if (s.from >= lo && s.to <= hi) out.push(i) })
  return out
}

export const segmentLabel = (n: Network, i: number): string =>
  `${n.stations[n.segments[i].from].name} – ${n.stations[n.segments[i].to].name}`

/**
 * How many Network Rail traversals a timetable makes — i.e. the paths it needs.
 *
 * Chris's constraint for Mode B is "you have what we do now": run the observed
 * timetable through this and you get today's path usage, which becomes the cap
 * Mode B has to live inside. That is a far more defensible budget than a number
 * someone guessed, and it makes the A/B comparison fair by construction.
 */
export function observedNrTraversals(n: Network, deps: { enabled: boolean; o: number; d: number }[]): number {
  const nrSegs = n.segments
    .map((s, i) => ({ s, i }))
    .filter(({ s }) => s.owner === 'networkrail')
  if (nrSegs.length === 0) return 0
  let total = 0
  for (const dep of deps) {
    if (!dep.enabled) continue
    const lo = Math.min(dep.o, dep.d)
    const hi = Math.max(dep.o, dep.d)
    for (const { s } of nrSegs) {
      if (s.from >= lo && s.to <= hi) total += s.pathsPerTraversal ?? 1
    }
  }
  return total
}

/** Apply a path budget to the Network Rail segments of an existing network. */
export function withPathBudget(n: Network, pathsPerDay: number): Network {
  if (!(pathsPerDay > 0)) return n
  return {
    ...n,
    segments: n.segments.map((s) =>
      s.owner === 'networkrail' ? { ...s, pathsPerDay } : s
    ),
  }
}
